import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type {
    Commerce,
    CommerceShareResource,
    CommerceShareTargetType,
} from "@teqtank/commerce-sdk";

@customElement("teqtank-share-button")
export class TeqtankShareButton extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property() public accessor type: CommerceShareTargetType = "storefront";
    @property({ attribute: "target-id" }) public accessor targetId = "";
    @property() public accessor label = "Share";
    @state() private accessor busy = false;
    public static override styles = css`
        :host {
            display: inline-block;
            font-family: Inter, ui-sans-serif, system-ui, sans-serif;
        }
        button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 0.4rem;
            padding: 0.7rem 1rem;
            border: 1px solid var(--teqtank-color-accent, #01676b);
            border-radius: 999px;
            background: var(--teqtank-color-accent, #01676b);
            color: #fff;
            font: inherit;
            font-weight: 700;
            cursor: pointer;
        }
        button:disabled {
            opacity: 0.65;
            cursor: wait;
        }
    `;
    protected override render() {
        return html`<button
            part="button"
            part="button"
            type="button"
            ?disabled=${this.busy}
            @click=${this.share}
        >
            ${this.busy ? "Preparing…" : this.label}
        </button>`;
    }
    private async share(): Promise<void> {
        if (!this.commerce || this.busy) return;
        this.busy = true;
        try {
            const resource: CommerceShareResource = await this.commerce.share.create({
                type: this.type,
                ...(this.targetId ? { id: this.targetId } : {}),
            });
            const shared = await this.commerce.share.native(resource).catch(() => false);
            if (!shared) await this.commerce.share.copy(resource);
            this.dispatchEvent(
                new CustomEvent("teqtank-share-created", {
                    detail: resource,
                    bubbles: true,
                    composed: true,
                }),
            );
        } finally {
            this.busy = false;
        }
    }
}
declare global {
    interface HTMLElementTagNameMap {
        "teqtank-share-button": TeqtankShareButton;
    }
}
